import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Button, Spinner, NavLink } from 'reactstrap';
import { GET_FACTORY_SETTINGS, GET_COMPANIES } from '../../query/query';
import {
  SET_FACTORY_SETTINGS_COLOR,
  SET_FACTORY_SETTINGS_COMPANY_ID,
  SET_FACTORY_SETTINGS_SCREEN_TYPE,
  SET_FACTORY_SETTINGS_PCB_VERSION,
} from '../../mutation/mutation';

const COLOR_MODE = Object.freeze({
  UNKNOWN: -1,
  BLACK_AND_WHITE: 0,
  COLOR: 1,
});

const SCREEN_TYPE = Object.freeze({
  EUROCOMPOSANT: 'Eurocomposant',
  TSD: 'TSD',
});

const PCB_VERSIONS = Object.freeze({
  VERSION1: 'version 1',
  VERSION2: 'version 2',
});

const BigTitle = ({ color = 'black', text }) => (
  <h1 style={{ color, fontSize: 100 }}>{text}</h1>
);

export default () => {
  const {
    data: { savGetFactorySettings } = {},
    loading: factorySettingsQueryLoading,
    error: { message: factorySettingsMessage } = {},
    startPolling: startFactorySettingsPolling,
    stopPolling: stopFactorySettingsPolling,
    refetch: refetchSettings,
  } = useQuery(
    GET_FACTORY_SETTINGS, { fetchPolicy: 'no-cache' },
  );
  const {
    data: { savGetCompanies } = {},
    loading: companiesQueryLoading,
    error: { message: companiesMessage } = {},
    startPolling: startCompaniesPolling,
    stopPolling: stopCompaniesPolling,
    refetch: refetchCompanies,
  } = useQuery(
    GET_COMPANIES, { fetchPolicy: 'no-cache' },
  );

  const [setColorMutation, { loading: colorMutationLoading }] = useMutation(SET_FACTORY_SETTINGS_COLOR);
  const [setCompanyIdMutation, { loading: companyIdMutationLoading }] = useMutation(SET_FACTORY_SETTINGS_COMPANY_ID);
  const [setScreenTypeMutation, { loading: screenTypeMutationLoading }] = useMutation(SET_FACTORY_SETTINGS_SCREEN_TYPE);
  const [setPcbVersionMutation] = useMutation(SET_FACTORY_SETTINGS_PCB_VERSION);

  const [color, setColor] = useState(COLOR_MODE.UNKNOWN);
  const [pcbVersion, setPcbVersion] = useState(PCB_VERSIONS.VERSION1);
  const [screenType, setScreenType] = useState(SCREEN_TYPE.EUROCOMPOSANT);
  const [companyId, setCompanyId] = useState(undefined);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setCompanyName(((savGetCompanies || []).find(({ _id }) => companyId === _id) || {}).name || '');
  }, [companyId, savGetCompanies]);

  useEffect(() => {
    if (!startFactorySettingsPolling) return (() => null);
    startFactorySettingsPolling(60 * 1000);
    return stopFactorySettingsPolling;
  }, [startFactorySettingsPolling]);

  useEffect(() => {
    if (!startCompaniesPolling) return (() => null);
    startCompaniesPolling(60 * 1000);
    return stopCompaniesPolling;
  }, [startCompaniesPolling]);

  useEffect(() => {
    if (factorySettingsMessage) setError(factorySettingsMessage);
    if (companiesMessage) setError(companiesMessage);
    else setError(false);
  }, [factorySettingsMessage, companiesMessage]);

  useEffect(() => {
    if (savGetFactorySettings) {
      setColor(savGetFactorySettings.color ? COLOR_MODE.COLOR : COLOR_MODE.BLACK_AND_WHITE);
      setCompanyId(savGetFactorySettings.companyId);
      setScreenType(savGetFactorySettings.screenType || SCREEN_TYPE.EUROCOMPOSANT);
      setPcbVersion(savGetFactorySettings.pcbVersion || PCB_VERSIONS.VERSION1);
    }
  }, [savGetFactorySettings]);

  useEffect(() => {
    setLoading(factorySettingsQueryLoading || companiesQueryLoading || colorMutationLoading || companyIdMutationLoading || screenTypeMutationLoading);
  }, [factorySettingsQueryLoading, companiesQueryLoading, colorMutationLoading, companyIdMutationLoading, screenTypeMutationLoading]);

  const toggleColor = useCallback(async () => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Are you sure to set factory in ${color === COLOR_MODE.COLOR ? 'black & white' : 'color'} mode?`)) {
      const { data: { savSetFactorySettingsColor } } = await setColorMutation({ variables: { color: color !== COLOR_MODE.COLOR } }) || {};
      setColor(savSetFactorySettingsColor.color ? COLOR_MODE.COLOR : COLOR_MODE.BLACK_AND_WHITE);
    }
  }, [setColorMutation, color, setColor]);

  const toggleCompanyId = useCallback(async (event) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Are you sure to set company to ${event.target.selectedOptions[0].label}?`)) {
      const { data: { savSetFactorySettingsCompanyId } } = await setCompanyIdMutation({ variables: { companyId: event.target.value === "undefined" ? undefined : event.target.value } }) || {};
      setCompanyId(savSetFactorySettingsCompanyId.companyId === null ? "undefined" : savSetFactorySettingsCompanyId.companyId);
    }
  }, [companyId, setCompanyId]);

  const changeScreenType = async (event) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Are you sure to change screen type ${event.target.selectedOptions[0].label}?`)) {
      const { data: { savSetFactorySettingsScreenType } } = await setScreenTypeMutation({ variables: { screenType: event.target.value } }) || {};
      setScreenType(savSetFactorySettingsScreenType.screenType || SCREEN_TYPE.EUROCOMPOSANT);
    }
  };

  const changePcbVersion = async (event) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Are you sure to change PCB ${event.target.selectedOptions[0].label}?`)) {
      const { data: { savSetFactorySettingsPcbVersion } } = await setPcbVersionMutation({ variables: { pcbVersion: event.target.value } }) || {};
      setPcbVersion(savSetFactorySettingsPcbVersion.pcbVersion || PCB_VERSIONS.VERSION1);
    }
  };

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      await refetchSettings();
      await refetchCompanies();
      setLoading(false);
    } catch (err) {
      setError(err);
    }
  }, [refetchSettings, setLoading]);

  return (
    <div
      style={{
        justifyContent: 'center', alignItems: 'center', display: 'flex',
        width: 1000, flexDirection: 'column',
      }}
    >
      {loading && <Spinner color="primary" />}
      {error && <p>Error: {error}</p>}
      <div
        style={{
          backgroundColor: color === COLOR_MODE.COLOR ? '#8A64FF' : 'white', width: 600, borderRadius: 30,
          justifyContent: 'center', alignItems: 'center', display: 'flex',
        }}
      >
        {color === COLOR_MODE.COLOR && <BigTitle color="white" text="COLOR" />}
        {color === COLOR_MODE.BLACK_AND_WHITE && <BigTitle text="BLACK & WHITE" />}
      </div>
      <div>
        {companyId && (<BigTitle text={companyName} />)}
      </div>
      <Button disabled={loading} color="secondary" onClick={refetch} style={{ marginTop: 30 }}>
        Refresh status
      </Button>
      {color !== COLOR_MODE.UNKNOWN && (
        <Button disabled={loading} color="primary" onClick={toggleColor} style={{ marginTop: 30 }}>
          {`${color === COLOR_MODE.COLOR ? 'Disable' : 'Enable'} color`}
        </Button>
      )}
      <div style={{ marginTop: 30 }}>
        <b>Company: </b>
        {savGetCompanies && (
          <select value={companyId} onChange={toggleCompanyId}>
            <option value="undefined">No company</option>
            {
              savGetCompanies.map(company => (
                <option key={company._id} value={company._id}>{company.name}</option>
              ))
            }
          </select>
        )}
      </div>
      <div style={{ marginTop: 30 }}>
        <b>Screen type: </b>
        <select value={screenType} onChange={changeScreenType}>
          {Object.values(SCREEN_TYPE).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div style={{ marginTop: 30 }}>
        <b>PCB Version: </b>
        <select value={pcbVersion} onChange={changePcbVersion}>
          {Object.values(PCB_VERSIONS).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <NavLink style={{ marginTop: 60 }} href="/factoryReset">Unregister Boxes</NavLink>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/factory/FactorySettingsPage.js